/**
 * Derives a provider's public acronym from their company name.
 *
 * Providers are identified by an acronym rather than their full company name
 * until the operator unlocks the directory for the viewer (client feedback
 * item 12). The acronym is DERIVED, not author-entered, so it works on the
 * listings that already exist without asking every provider to supply one.
 *
 * The two worked examples the client gave pin down the rules:
 *   "Accra Food Safety Consulting Ltd" -> AFSC   (legal suffix dropped)
 *   "AgriQuality Labs"                 -> AQL    (AgriQuality splits to Agri + Quality)
 *
 * The second is the load-bearing one: a naive first-letter-per-word split gives
 * "AL", so internal capitals have to split too.
 */

// Dropped before initials are taken. Legal forms carry no identifying signal and
// would otherwise put an L on the end of half the directory.
const LEGAL_SUFFIXES = new Set([
  'ltd',
  'limited',
  'llc',
  'llp',
  'lp',
  'inc',
  'incorporated',
  'plc',
  'corp',
  'corporation',
  'co',
  'company',
  'gmbh',
  'bv',
  'nv',
  'sa',
  'sarl',
  'srl',
  'ag',
  'as',
  'oy',
  'ab',
  'pty',
  'pvt',
  'enterprise',
  'enterprises',
  'holdings',
  'group',
]);

// Grammatical filler. "Institute of Food Science" should read IFS, not IOFS.
const STOP_WORDS = new Set(['and', 'of', 'the', 'for', 'in', 'on', 'at', 'to', 'a', 'an', '&']);

const MAX_LENGTH = 5;

/**
 * Split a name into the words that contribute an initial.
 *
 * Splits on whitespace and punctuation, then again inside each token at
 * lower->upper boundaries so camel-cased names ("AgriQuality", "LabelPro",
 * "FoodTech") yield their component words. An all-caps token is left whole:
 * "PFS" is one word, not three.
 */
const toSignificantWords = name =>
  `${name}`
    .split(/[^A-Za-z0-9&]+/)
    .filter(Boolean)
    .flatMap(token =>
      // Only split a token that actually mixes case; ALLCAPS and lowercase pass through.
      /[a-z]/.test(token) && /[A-Z]/.test(token)
        ? token.replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(' ')
        : [token]
    )
    .filter(word => {
      const lower = word.toLowerCase();
      return !LEGAL_SUFFIXES.has(lower) && !STOP_WORDS.has(lower);
    });

/**
 * @param {string} companyName the provider's full company name (the listing title)
 * @returns {string} the acronym, or '' when nothing usable can be derived
 */
export const acronymFromName = companyName => {
  if (typeof companyName !== 'string' || !companyName.trim()) {
    return '';
  }

  const words = toSignificantWords(companyName);

  // A name that is nothing but legal suffixes and filler ("The Company Ltd")
  // would otherwise derive to ''. Fall back to the raw tokens so the provider
  // still gets an identifier rather than an empty label.
  const source = words.length > 0 ? words : `${companyName}`.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (source.length === 0) {
    return '';
  }

  // A single remaining word has no initials to gather, so take its opening
  // letters instead: "Bakeries" -> BAK. Two chars would collide constantly.
  if (source.length === 1) {
    return source[0].slice(0, 3).toUpperCase();
  }

  return source
    .map(word => word.charAt(0))
    .join('')
    .slice(0, MAX_LENGTH)
    .toUpperCase();
};

/**
 * Whether this viewer may see one provider's full company name.
 *
 * The unlock is the INQUIRY, not the viewer's approval status. Two reasons:
 *
 * 1. It is the most literal reading of the feedback — "full name is revealed
 *    only after payment" — in a marketplace that takes no payments. Here the
 *    inquiry is the transaction, so the inquiry is the moment of engagement.
 *
 * 2. Approval cannot be the unlock once the marketplace is private
 *    (`marketplace.private` + `users.requireApprovalToJoin`, the access-control
 *    half of this feature). Under that setting every member who can browse at
 *    all is already approved, so gating on approval would unlock every name for
 *    everyone and this whole feature would be dead code.
 *
 * A provider always sees their own listing's real name — being shown their own
 * acronym would read as their name having been lost.
 *
 * @param {Object} params
 * @param {string} params.listingId the provider profile listing's uuid
 * @param {Array<string>} [params.contactedListingIds] from ducks/contactedProviders.duck
 * @param {boolean} [params.isOwnListing]
 * @returns {boolean}
 */
export const canSeeProviderName = ({
  listingId,
  contactedListingIds = [],
  isOwnListing = false,
}) => isOwnListing || (!!listingId && contactedListingIds.includes(listingId));

/**
 * The label to show for a provider.
 *
 * Centralised so every surface (search cards, listing page, message threads,
 * reviews) resolves the name the same way, and so the gate can be tightened in
 * one place later.
 *
 * @param {Object} params
 * @param {string} params.companyName the full name (listing title)
 * @param {boolean} params.canSeeFullName whether this viewer has been unlocked
 * @param {boolean} [params.isOwnListing] a provider always sees their own real name
 * @returns {string} the full name when unlocked, otherwise the acronym
 */
export const providerDisplayName = ({ companyName, canSeeFullName, isOwnListing = false }) => {
  if (canSeeFullName || isOwnListing) {
    return companyName;
  }
  return acronymFromName(companyName) || companyName;
};

/**
 * Coarsen a street address to a city/region/country label.
 *
 * `publicData.location.address` is whatever the geocoder returned for what the
 * provider typed, so one provider's card read "Kumasi, Ashanti, Ghana" while
 * another's printed "Daddy Adediran Street 5, Lagos 10, Lagos, Nigeria" — the
 * second is a doorstep, published to anyone browsing. Gating the name while
 * leaving that in place identifies the company just as well.
 *
 * Segments carrying digits are dropped (street numbers, plot numbers, post
 * codes), then at most the last three are kept, which lands on "Lagos, Nigeria"
 * for the address above and leaves an already-coarse one untouched.
 *
 * @param {string} address publicData.location.address
 * @returns {string} the coarsened label, or '' when nothing usable remains
 */
export const coarseLocation = address => {
  if (typeof address !== 'string' || !address.trim()) {
    return '';
  }
  const segments = address
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .filter(s => !/\d/.test(s));

  return segments.slice(-3).join(', ');
};

/**
 * The location label to show for a provider, gated like the name.
 *
 * @param {Object} params
 * @param {string} [params.address] publicData.location.address
 * @param {string} [params.country] the vetting `country` value, used as fallback
 * @param {boolean} params.canSeeFullName whether this viewer has been unlocked
 * @returns {string|null} the label, or null when there is nothing to show
 */
export const providerDisplayLocation = ({ address, country, canSeeFullName }) => {
  if (canSeeFullName) {
    return address || country || null;
  }
  return coarseLocation(address) || country || null;
};
